import React, { useState } from 'react'
import { displayNotification } from '../../../actions/notificationsActions'
import axios from 'axios'
import LoaderButton from '../../../components/loaderButton'
import { connect } from 'react-redux'
import { UserVehicleLink } from '../userVehicleLinkHOC'
import CardWrapper from './CardWrapper'
import history from '../../../history'
import { Link } from 'react-router-dom'
import { Product, ProductOption } from '../../refiTransactions/types'
import { ApiResponse, ProductPackage, User, Vehicle } from '../../../core/types'
import { Dispatch } from 'redux'
import { ModalWrapper } from '../../../components/modalWrapper'
import { ApiReponses } from '../../../components/apiResponses'
import { AgreementLink } from './AgreementLink'
import ApplyForFinancingBtn from './ApplyForFinancingBtn'
import BackOutDownPaymentBtn from './BackOutDownPaymentBtn'
import { Tooltip } from '../../../chassis/tooltip'
import InfoIcon from '../../../assets/images/info.svg'
import { selectCurrentUserBadges } from '../../../reducers'
import { request } from '../../../helpers/apiHelper'
import { productPackageApiResponsesQuery } from '../queries'
import InsuranceQuoteBtn from './InsuranceQuoteBtn'
import InsuranceCaptureBtn from './InsuranceCaptureBtn'
import { capitalize, startCase } from 'lodash'

const statusToAvailableBack = ['empty', 'selected', 'quoted', 'booked', 'payment_failed', 'product_remitted_error']

interface ProductOptionWithDescription extends ProductOption {
  description: string
}

interface ProductWithNewOptionName extends Product {
  selectedProductOption: ProductOptionWithDescription
}

interface Envelope {
  id: string
  slug: string
  status: string
  url: string
}

interface DocumentSubmission {
  id: string
  docType: string
  createdAt: string
  viewedAt: string | null
  signedAt: string | null
  url: string
}

// What an imported historical quote was quoted. Those quotes stay `empty`
// with no products, so this is the only place the plan shows.
interface LegacyQuotedPlan {
  planName: string | null
  price: number | null
  financeTerm: number | null
  administratorName: string | null
}

interface ProductWithVehicle extends ProductPackage {
  customerToken: string
  membershipId: number
  organization?: {
    id: string,
    nameLegal: string
  }
  vehicle: Vehicle
  downPayment: number
  envelope?: Envelope
  monthlyPayment: number
  paymentCount: number
  products: ProductWithNewOptionName[]
  remittedAt: string | null
  remitError: string | null
  remitErrorAt?: string | null
  vinMatchesProduct: boolean
  vinValidationMessage: string | null
  productAgreement?: DocumentSubmission
  paymentAgreement?: DocumentSubmission
  admin?: User
  legacyQuotedPlan?: LegacyQuotedPlan | null
}

export type Callback<T = void> = (...args: any) => T

export interface Props {
  productPackage: ProductWithVehicle
  user: User
  dispatch: Dispatch
  internalUrl: string | null
  currentUserBadges?: string[]
}

// The term is not repeated: the view parses it out of the plan name, which
// already reads "Diamond 48/75,000".
export const previouslyQuotedText = (plan: LegacyQuotedPlan) =>
  [
    plan.planName,
    plan.price != null &&
      `$${plan.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    plan.financeTerm &&
      `${plan.financeTerm} ${plan.financeTerm === 1 ? 'payment' : 'payments'}`,
    plan.administratorName,
  ]
    .filter(Boolean)
    .join(' · ')

export const ProductInfo = ({ product }) => {
  const { description: productDescription, selectedProductOption } = product
  if (selectedProductOption) {
    const {
      deductible,
      description: optionDescription,
      surcharges,
    } = selectedProductOption

    return (
      <>
        <li>{productDescription}</li>
        <li>{deductible}</li>
        <li>{optionDescription}</li>
        {surcharges.map((surcharge) => (
          <li key={surcharge.SurchargeCode}>+ {surcharge.Description}</li>
        ))}
      </>
    )
  } else {
    return null
  }
}

const SendEmailBtn = ({ productPackage, user, dispatch }) => {
  const [loading, setLoading] = useState(false)

  const btnProps = {
    onClick: async () => {
      if (loading) return
      try {
        setLoading(true)
        await axios.post(
          `${process.env.REACT_APP_BLINKER_API_URL}/api/v3/admin/products/payments/send_payment_email/${productPackage.id}?user_id=${user.id}`
        )
        dispatch(displayNotification('Email Sent Successfully', 'success'))
        setLoading(false)
      } catch (error) {
        setLoading(false)
        dispatch(
          displayNotification(`Error Sending Email - ${error.message}`, 'error')
        )
      }
    },
    children: loading ? 'Sending email...' : 'Send VSC payment tool via email',
    className: loading ? 'disabled-link' : 'user-account__link',
  }

  return <a {...btnProps} />
}

const VehicleWithProductPackageListItem = ({
  productPackage,
  user,
  dispatch,
  internalUrl,
  currentUserBadges,
}: Props) => {
  const {
    customerToken,
    status,
    membershipId,
    downPayment,
    monthlyPayment,
    paymentCount,
    vehicle,
    products,
    vinMatchesProduct,
    vinValidationMessage,
    productAgreement,
    paymentAgreement,
    admin,
    organization,
    legacyQuotedPlan,
  } = productPackage

  const handleClick = async () => {
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BLINKER_API_URL}/api/v3/products/packages/${productPackage.id}/get_latest_status`
    );

    const data = response.data;
    const message = 'Package status has changed. Please refresh to see the latest status.'

    if (statusToAvailableBack.includes(data.status) || (data.vin_matched === false && data.status === 'payment_succeeded')) {
      history.push(`/refiapp?packageId=${productPackage.id}`);
    }
    else{
      dispatch(displayNotification(message, 'error'));
      setTimeout(() => {
              window.location.href = user.id
      }, 4000)
    }

  } catch (error) {
    console.error("Failed to fetch latest status:", error);
  }
};


  const collectPayment = () => {
    history.push(`/refiapp/payment?packageId=${productPackage.id}`)
  }

  const isBlinkerAdmin = (currentUserBadges || []).some((badge) =>
    ['ADMIN', 'SUPERUSER', 'ADMIN_WEB'].includes(badge)
  )
  const canContinueAsCustomer = (currentUserBadges || []).includes('CONTINUE_AS_CUSTOMER')

  // Modal state for ApiResponses. The payloads are large raw third party
  // request/response bodies, so they are fetched on demand rather than with
  // the rest of the user profile.
  const [showApiModal, setShowApiModal] = useState(false)
  const [apiResponses, setApiResponses] = useState<ApiResponse[]>([])
  const [loadingApiResponses, setLoadingApiResponses] = useState(false)

  const openApiResponses = async () => {
    setShowApiModal(true)

    if (apiResponses.length > 0 || loadingApiResponses) return

    setLoadingApiResponses(true)
    try {
      const response = await request(productPackageApiResponsesQuery, {
        id: productPackage.id,
      })
      if (response.data.errors) {
        throw new Error(response.data.errors[0].message)
      }
      setApiResponses(response.data.data.productPackage.apiResponses || [])
    } catch (error) {
      setShowApiModal(false)
      dispatch(
        displayNotification(
          `Failed to load API responses - ${error.message}`,
          'error'
        )
      )
    } finally {
      setLoadingApiResponses(false)
    }
  }

  let vehicleAttrs: string[] = []
  const year = vehicle?.year ?? vehicle?.trimSyncMetadata?.year
  if (year != null) vehicleAttrs.push(String(year))

  const make = vehicle?.make ?? vehicle?.trimSyncMetadata?.make?.toUpperCase()
  if (make) vehicleAttrs.push(make)

  const model =
    vehicle?.model ?? vehicle?.trimSyncMetadata?.model?.toUpperCase()
  if (model) vehicleAttrs.push(model)

  if (vehicle?.selectedTrim?.name) vehicleAttrs.push(vehicle.selectedTrim.name)
  if (vehicle?.selectedTrim?.style)
    vehicleAttrs.push(vehicle.selectedTrim.style)

  if (!vehicle?.selectedTrim?.name && !vehicle?.selectedTrim?.style && vehicle?.trimSyncMetadata?.trim)
    vehicleAttrs.push(vehicle?.trimSyncMetadata?.trim.toUpperCase())

  let vehicleLabel = vehicleAttrs.join(' ')
  
  const continueAsCustomerUrl = admin?.email ?`${process.env.REACT_APP_BLINKER_API_URL}/packages/${customerToken}?agent_email=${encodeURIComponent(admin.email)}`: `${process.env.REACT_APP_BLINKER_API_URL}/packages/${customerToken}`
  return (
    <CardWrapper
      leftText={`ID ${`${productPackage.id}`}`}
      pillText={productPackage.status && productPackage.status.toUpperCase()}
      internalUrl={internalUrl}
    >
      {isBlinkerAdmin && (
        <div>
          <button
            type="button"
            onClick={openApiResponses}
            disabled={loadingApiResponses}
            style={{ marginBottom: 8 }}
          >
            {loadingApiResponses
              ? 'Loading API Responses...'
              : 'View API Responses'}
          </button>
          {showApiModal && !loadingApiResponses && (
            <ModalWrapper
              title="API Responses"
              titleClass="h1"
              hideModal={() => setShowApiModal(false)}
              width="900px"
              height="600px"
              xclose={true}
            >
              <ApiReponses apiResponses={apiResponses} />
            </ModalWrapper>
          )}
        </div>
      )}
      {!vinMatchesProduct && vinValidationMessage && (
        <li
          className="user-vehicles__line status-pill--yellow"
          style={{ paddingTop: 0, textAlign: 'center' }}
        >
          VIN validation failed: {vinValidationMessage}
        </li>
      )}
      <li className="user-vehicles__line">{vehicleLabel}</li>
      <li>DA Membership: {`${membershipId == null ? '-' : membershipId}`}</li>
      {products?.map((product) => (
        <ProductInfo product={product} key={product.id} />
      ))}
      {legacyQuotedPlan && (
        <li className="user-vehicles__line">
          Previously quoted: {previouslyQuotedText(legacyQuotedPlan)}
        </li>
      )}
      <li className="user-vehicles__line">
        Down Payment: {`${downPayment ? downPayment : '-'}`}
      </li>
      <li className="user-vehicles__line">
        Monthly Payments({`${paymentCount ? paymentCount : '-'}`}):{' '}
        {`${monthlyPayment ? monthlyPayment : '-'}`}
      </li>
      {admin?.name && (
        <li className="user-vehicles__line">
          Owner: {`${admin?.name ? startCase(capitalize(admin.name)) : '-'}`}
        </li>
      )}
      {organization?.nameLegal && (
        <li className="user-vehicles__line">
          Organization: {`${organization?.nameLegal ? startCase(capitalize(organization.nameLegal)) : '-'}`}
        </li>
      )}
      {statusToAvailableBack.includes(status) || !vinMatchesProduct ? (
        <LoaderButton
          type="button"
          className="button button-drive-america continue-application-drive-america"
          onClick={handleClick}
        >
          Continue Application
        </LoaderButton>
      ) : null}
      {!productPackage.paidAt && status === 'booked' && (
        <LoaderButton
          type="button"
          className="button button-drive-america continue-application-drive-america"
          onClick={collectPayment}
        >
          Collect Payment
        </LoaderButton>
      )}

      {canContinueAsCustomer &&
        productPackage?.id &&
        productPackage?.customerToken &&
        productPackage?.paymentCount && (
          <LoaderButton
            type="button"
            className="button button-drive-america continue-application-drive-america"
            onClick={() =>
              open(
                continueAsCustomerUrl,
                window.self !== window.top ? '_self' : '_blank'
              )
            }
          >
            Continue as Customer
          </LoaderButton>
        )}

      {productAgreement && (
        <AgreementLink
          productPackage={productPackage}
          agreement={productAgreement}
          dispatch={dispatch}
          provideData={{
            url: 'https://scsautoexpress.com/scs.dap.omga/ContractSearch.aspx?contract=',
            textData: 'Navigate to Product Provider',
          }}
        />
      )}

      {paymentAgreement && (
        <AgreementLink
          productPackage={productPackage}
          agreement={paymentAgreement}
          dispatch={dispatch}
          provideData={{
            url: 'https://portal.ensuretyfinance.com/contract/',
            textData: 'Navigate to Payment Provider',
          }}
        />
      )}
      <BackOutDownPaymentBtn
        productPackage={productPackage}
        user={user}
        dispatch={dispatch}
        currentUserBadges={currentUserBadges}
      />
      <ApplyForFinancingBtn
        productPackage={productPackage}
        user={user}
        dispatch={dispatch}
      />
      <InsuranceQuoteBtn
        productPackage={productPackage}
        user={user}
        dispatch={dispatch}
      />
      <InsuranceCaptureBtn
        productPackage={productPackage}
        user={user}
        dispatch={dispatch}
      />
    </CardWrapper>
  )
}

const mapStateToProps = (state) => ({
  currentUserBadges: selectCurrentUserBadges(state) || [],
})

const mapDispatchToProps = (dispatch) => ({
  dispatch,
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(UserVehicleLink(VehicleWithProductPackageListItem))
